import Template from "../../components/Template";
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import config from "../../config";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { Link } from "react-router-dom";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";

const Documentos = () => {
    const navigate = useNavigate();
    const [categorias, setCategorias] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const { updateMenu } = useNavigation();
    const [documentos, setDocumentos] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null); // Estado para o documento selecionado
    const [showDialog, setShowDialog] = useState(false); // Estado para controlar o modal    
    const [selectedPdf, setSelectedPdf] = useState("");
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalRecords, setTotalRecords] = useState(0); // Total de registros    
    const menuRef = useRef(null);
    const menuFiltrarRef = useRef(null);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const [showFilesListModal, setShowFilesListModal] = useState(false); // Controla a visibilidade da nova modal
    const [currentDocumentFiles, setCurrentDocumentFiles] = useState(null); // Armazena os arquivos do documento selecionado    

    const { filters, setFilters, searchTerm, setSearchTerm, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('documentos', {
        categoria: "",
        status: "",
        nomeDocumento: "",
        validadeInicial: "",
        validadeFinal: "",
    });
    const [filteredDocumentos, setFilteredDocumentos] = useState([]);
    const [message, setMessage] = useState("");

    useEffect(() => {
        // Carregar categorias e status da API
        const fetchData = async () => {
            try {
                const [categoriasRes, statusRes] = await Promise.all([
                    api.get("/categorias"),
                    api.get("/statuses"),
                ]);
                setCategorias(categoriasRes.data);
                setStatusList(statusRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchDocumentos(currentPage, pageSize);
    }, [currentPage]);

    useEffect(() => {
        // Sempre que o termo de pesquisa mudar, atualize os documentos filtrados
        if (searchTerm.trim() === "") {
            setFilteredDocumentos(documentos); // Se não houver termo de pesquisa, mostre todos os documentos
        } else {
            setFilteredDocumentos(
                documentos.filter((doc) =>
                    doc.nomeDocumento.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [searchTerm, documentos]);

    const fetchDocumentos = async (page, size) => {
        try {
            const response = await api.get(`/documentos?page=${page}&pageSize=${size}`);
            setDocumentos(response.data.records);
            setTotalRecords(response.data.total);
            setPageSize(size); // Atualiza o tamanho
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
        }
    };

    const fetchFilteredDocumentos = async (query) => {
        try {
            const params = new URLSearchParams(query).toString();
            const response = await api.get(`/documentos?${params}`);

            setDocumentos(response.data.records);
            setTotalRecords(response.data.total);
            setCurrentPage(1);
        } catch (error) {
            console.error("Erro ao aplicar filtros:", error);
        }
    };

    const handleNavigateCadastrar = async (e) => {
        e.preventDefault();
        try {
            navigate('/documentos/cadastrar');
        } catch (err) {
            console.error('Documentos Error:', err);
            alert(err.response?.data?.msg || 'Erro ao ir para o cadastro');
        }
    };

    const handleCloseDialog = () => {
        setSelectedDoc(null);
        setShowDialog(false);
    };

    const handleShowPdf = (fullUrl) => { // Certifique-se que o argumento é a URL completa
        setSelectedPdf(fullUrl);
        setShowDialog(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    // Janela de páginas exibidas
    const pageWindowSize = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);

    const pageNumbers = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
    );

    const [columns, setColumns] = useState([
        {
            id: "id",
            label: "ID Documento",
            visible: true,
            formatter: (value) => (
                <Link to={`/documentos/editar/${value}`} className="text-stone-600 hover:underline">
                    {value}
                </Link>
            ),
        },
        {
            id: "nomeDocumento",
            label: "Nome do Documento",
            visible: true,
            formatter: (value) => value, // Nenhuma formatação
        },
        {
            id: "categoria",
            label: "Tipo",
            visible: true,
            formatter: (value) => value?.nome || "", // Formata como nome da categoria
        },
        {
            id: "validade",
            label: "Validade",
            visible: true,
            formatter: (value) =>
                value ? formatDateWithoutHour(value) : "", // Formata data
        },
        {
            id: "dataUpload",
            label: "Data do Upload",
            visible: true,
            formatter: (value) =>
                value ? formatDateWithHour(value) : "", // Formata data com hora
        },
        {
            id: "status",
            label: "Status",
            visible: true,
            formatter: (value) => value?.nome || "", // Formata como nome do status
        },
        {
            id: "criadoPor",
            label: "Responsável pelo Upload",
            visible: true,
            formatter: (value) => value?.nome || "", // Formata como nome do criador
        },
        {
            id: "arquivos",
            label: "Ver",
            visible: true,
           // O formatter agora recebe o objeto `doc` completo para acessar `doc.id` e `doc.arquivos`
            formatter: (arquivos, doc) => ( // O segundo argumento é o objeto `doc` completo da linha
                <div
                    className="w-10 h-10 relative cursor-pointer flex justify-center items-center"
                    onClick={() => handleShowDocumentFilesList(doc)} // Chama a nova função, passando o documento inteiro
                    title="Ver arquivos do documento"
                >
                    {arquivos && Array.isArray(arquivos) && arquivos.length > 0 ? (
                        <img
                            src="images/icon/search.png" // Ícone de lupa
                            className="w-10 h-10"
                            alt="Ver Documento"
                        />
                    ) : (
                        <span>-</span> // Exibe um traço se não houver arquivos
                    )}
                </div>
            ),
        },
    ]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuFiltrarOpen, setMenuFiltrarOpen] = useState(false);

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    // Função para fechar o menu ao clicar fora
    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);

        };
    }, [menuOpen]);

    const handleMenuClick = (event) => {
        // Impede a propagação do evento para o handleClickOutside
        event.stopPropagation();
    };

    const applyFilters = () => {
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };

        fetchFilteredDocumentos(query);
    };

    const clearFilters = () => {
        clearPersistedState();
        setSearchTerm(""); // Limpa o termo de pesquisa
        setCurrentPage(1); // Reinicia a páginação

        setFilters({
            categoria: "",
            status: "",
            nomeDocumento: "",
            validadeInicial: "",
            validadeFinal: "",
        });
        fetchDocumentos(1, pageSize); // Restaura os dados padrão
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reinicia para a primeira página
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };
        fetchFilteredDocumentos(query); // Executa a busca com o termo atual
    };

    // Função para exportar a tabela
    const exportToExcel = () => {
        if (documentos.length === 0) {
            setMessage('Não há dados para exportar!')
            setShowSuccessDialog(true);
            return;
        }

        // Mapeia os dados da tabela para um formato plano
        const dataToExport = documentos.map((doc) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible) {
                    row[col.label] = col.formatter ? col.formatter(doc[col.id]) : doc[col.id];
                }
            });
            return row;
        });

        // Cria uma nova planilha do Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Cria um novo workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Documentos");

        // Exporta o arquivo
        XLSX.writeFile(workbook, "documentos.xlsx");
    };

    const handleShowDocumentFilesList = (doc) => {
        setCurrentDocumentFiles(doc); // Armazena o objeto do documento completo (com os arquivos)
        setShowFilesListModal(true); // Abre a nova modal
    };

    const handleCloseFilesListModal = () => {
        setShowFilesListModal(false);
        setCurrentDocumentFiles(null);
    };    

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium font-['Inter']">Documentos</div>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Ver</div>
                        <select
                            className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                            value={pageSize} // O valor do tamanho da página atual
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10); // Converte para número
                                setPageSize(newPageSize); // Atualiza o tamanho da página no estado
                                setCurrentPage(1); // Reinicia para a primeira página
                                fetchDocumentos(1, newPageSize); // Atualiza os dados da API
                            }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Resultados</div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex" >
                                <img className="w-6 h-6 relative " src="images/icon/columns.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Colunas</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {menuOpen === "colunas" && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
                                    style={{
                                        zIndex: 9999, // Garante que está acima de outros elementos
                                    }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div
                                                key={col.id}
                                                className="flex items-center gap-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() =>
                                                        toggleColumnVisibility(col.id)
                                                    }
                                                />
                                                <label
                                                    htmlFor={col.id}
                                                    className="text-sm text-gray-700"
                                                >
                                                    {col.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")} >
                            <div className="justify-start items-center gap-2 flex">
                                <img className="w-8 h-8 px-0.5 py-[3px] justify-center items-center flex" src="images/icon/filter.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Filtrar</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {/* Menu Suspenso */}
                            {menuOpen === "filtro" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="self-stretch justify-start items-center gap-2.5 inline-flex">
                                            <div className="grow shrink basis-0 text-stone-800 text-xl font-medium">
                                                Filtrar
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Categoria
                                            </div>
                                            <select
                                                name="categoria"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.categoria}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, categoria: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {categorias.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Status
                                            </div>
                                            <select
                                                name="status"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.status}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, status: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {statusList.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Nome do Documento
                                            </div>
                                            <input
                                                type="text"
                                                name="nomeDocumento"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o nome do documento"
                                                value={filters.nomeDocumento}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, nomeDocumento: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Validade
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    name="validadeInicial"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.validadeInicial}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, validadeInicial: e.target.value })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="validadeFinal"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.validadeFinal}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, validadeFinal: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="self-stretch flex items-center justify-between gap-4 mt-4">
                                            <button
                                                className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 justify-center items-center gap-2 flex"
                                                onClick={clearFilters}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/eraser.png" />
                                                <div className="text-stone-500 text-sm font-medium font-['Inter'] leading-normal" >Limpar</div>
                                            </button>
                                            <button
                                                className="px-4 py-2 w-full bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                                onClick={applyFilters}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/filter-white.png" />
                                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Filtrar</div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="justify-start items-center gap-2 flex">
                            {!isSearchActive ? (
                                // Conteúdo original da div "Pesquisar"
                                <div
                                    className="justify-start items-center gap-2 flex cursor-pointer"
                                    onClick={() => setIsSearchActive(true)} // Ativar o estado de pesquisa
                                >
                                    <img className="w-10 h-10 relative" src="images/icon/search.png" alt="Ícone Pesquisar" />
                                    <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                </div>
                            ) : (
                                // Conteúdo alternativo ao ativar o estado de pesquisa
                                <div className="h-10 justify-start items-center gap-2 inline-flex">
                                    <div className="h-9 px-3 py-2 bg-white rounded-md border border-slate-300 justify-start items-center flex">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar"
                                            className="w-full text-slate-400 text-base font-normal font-['Inter'] leading-tight focus:outline-none"
                                            value={filters.nomeDocumento}
                                            onChange={(e) =>
                                                setFilters({ ...filters, nomeDocumento: e.target.value })
                                            }
                                        />
                                        <img className="w-6 h-6 p-[3.33px] justify-center items-center flex cursor-pointer" src="images/icon/close.png" onClick={() => {
                                            setIsSearchActive(false);
                                        }} />
                                    </div>
                                    <button
                                        className="px-4 py-2 bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                        onClick={handleSearch}
                                    >
                                        <img className="w-4 h-4 relative" src="images/icon/search-white.png" alt="Ícone Pesquisar" />
                                        <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="self-stretch flex justify-between gap-4">
                        {usePermission('EXPORTAR_DOCUMENTO') && (
                            <button type='button' className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer" onClick={exportToExcel}>
                                <img className="w-4 h-4" src="images/icon/upload.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Exportar</div>
                            </button>
                        )}
                        {usePermission('CRIAR_DOCUMENTO') && (
                            <button type='button' className="px-4 py-2 bg-stone-600 text-white rounded-md flex items-center gap-2" onClick={handleNavigateCadastrar}>
                                <img className="w-4 h-4" src="images/icon/plus-circle.png" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Cadastrar Documento</div>
                            </button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto md:overflow-x-visible">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                {columns
                                    .filter((col) => col.visible)
                                    .map((col) => (
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">{col.label}</th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocumentos.map((doc) => (
                                <tr key={doc.id}>
                                    {columns
                                        .filter((col) => col.visible)
                                        .map((col) => {
                                            // Passa o valor da coluna E o objeto `doc` completo para o formatter
                                            return <td key={`${doc.id}-${col.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{col.formatter(doc[col.id], doc)}</td>;
                                        })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-slate-500 text-base font-normal font-['Inter'] leading-[29.60px]">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords}{" "} registros.
                    </div>
                    <div className="justify-start items-start gap-4 flex">
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                                }`}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-left.png" />
                        </button>

                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded  justify-center items-center flex text-base font-semibold font-['Inter'] ${page === currentPage
                                    ? "bg-stone-600 text-white"
                                    : "bg-gray-200 text-gray-600"
                                    }`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex 
                        ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-right.png" />
                        </button>
                    </div>
                </div>

                {showFilesListModal && currentDocumentFiles && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-4 md:p-10 flex flex-col justify-center items-center">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl h-auto max-h-[90vh] flex flex-col shadow-lg">
                            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-stone-800">Arquivos do Documento: {currentDocumentFiles.nomeDocumento}</h2>
                                <img
                                    className="w-6 h-6 cursor-pointer"
                                    src="images/icon/x.png"
                                    alt="Fechar"
                                    onClick={handleCloseFilesListModal}
                                />
                            </div>
                            <div className="flex-grow overflow-y-auto mt-4 pr-2"> {/* Adicionado pr-2 para scrollbar */}
                                {currentDocumentFiles.arquivos && currentDocumentFiles.arquivos.length > 0 ? (
                                    <ul className="space-y-3">
                                        {currentDocumentFiles.arquivos.map((file) => (
                                            <li key={file.id} className="flex justify-between items-center bg-gray-50 p-3 rounded-md border border-gray-200">
                                                <span className="text-sm font-medium text-stone-700 break-all pr-2">
                                                    {file.nomeArquivoOriginal}
                                                </span>
                                                <div className="flex-shrink-0 flex items-center gap-3">
                                                    {/* Botão Ver */}
                                                    <button
                                                        onClick={() => {
                                                            handleShowPdf(config.urlArquivo(file.caminhoArquivo)); // Apenas abre a modal do PDF // Fecha esta modal ao abrir a do PDF
                                                        }}
                                                        className="p-1 rounded-full hover:bg-gray-200"
                                                        title="Visualizar"
                                                    >
                                                        <img src="images/icon/search.png" className="w-5 h-5" alt="Ver" />
                                                    </button>
                                                    {/* Botão Download */}
                                                    <a
                                                        href={config.urlArquivo(file.caminhoArquivo)}
                                                        download
                                                        
                                                        rel="noopener noreferrer"
                                                        className="p-1 rounded-full hover:bg-gray-200"
                                                        title="Baixar"
                                                    >
                                                        <img src="images/icon/download.png" className="w-5 h-5" alt="Download" />
                                                    </a>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-stone-600 text-center py-4">Nenhum arquivo anexado a este documento.</p>
                                )}
                            </div>
                            <div className="pt-4 border-t border-gray-200 mt-4 text-right">
                                <button
                                    onClick={handleCloseFilesListModal}
                                    className="px-5 py-2 bg-stone-600 text-white rounded-md hover:bg-stone-700"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={`${selectedPdf}`} download target="_blank">
                                        <img className="w-6 h-6 relative  overflow-hidden" src="images/icon/download.png" />
                                    </a>
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                </div>
                                <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="images/icon/x.png" onClick={handleCloseDialog} />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${selectedPdf}#toolbar=0`} // CORRIGIDO: Removido a concatenação duplicada
                                    className="w-full h-full border-0"
                                    style={{ border: 'none' }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    )
}
export default Documentos;